import type { Field, FieldId } from '@eformulieren/model'
import * as Y from 'yjs'
import { orderedRows, richtextFragment, rowFragment, rowsList } from '../document/sectionDocument.js'
import type { CitationCluster } from './types.js'

/**
 * De attributen van een `referentie`-knoop in Yjs (naar `richtext/referentie.ts`).
 *
 * `Y.XmlElement` is standaard getypeerd op string-attributen (`{ [key: string]: string }`);
 * zonder dit type-argument zou `bronnen` als `string` gelezen worden terwijl het een array is.
 */
interface ReferenceAttrs {
  [key: string]: string | string[]
  citatieId: string
  bronnen: string[]
}

/**
 * Verzamelt de citatieclusters uit rich-text-fragmenten, in documentvolgorde.
 *
 * **Rechtstreeks op de Yjs-XML-boom en niet op een TipTap-editor**, in tegenstelling tot
 * CitatenPOC's `collectClusters` (die op `editor.state.doc.descendants` leest). Dat moet ook
 * werken zonder gemonteerde editor: de uitgeschreven stand van een revisie heeft geen editor,
 * alleen het document (opdracht G, R-95).
 */
export function collectClustersFromFragment(fragment: Y.XmlFragment): CitationCluster[] {
  const clusters: CitationCluster[] = []
  collectIn(fragment, clusters)
  return clusters
}

function collectIn(
  node: Y.XmlFragment | Y.XmlElement | Y.XmlText | Y.XmlHook,
  out: CitationCluster[],
): void {
  if (node instanceof Y.XmlText || node instanceof Y.XmlHook) return
  if (node instanceof Y.XmlElement && node.nodeName === 'referentie') {
    const reference = node as Y.XmlElement<ReferenceAttrs>
    const citationId = reference.getAttribute('citatieId')
    const sources = reference.getAttribute('bronnen')
    if (typeof citationId === 'string' && Array.isArray(sources)) {
      out.push({
        citatieId: citationId,
        bronnen: sources.filter((sourceId): sourceId is string => typeof sourceId === 'string'),
      })
    }
    // Een atoomknoop draagt geen kinderen; niets om verder in af te dalen.
    return
  }
  for (const child of node.toArray()) collectIn(child, out)
}

/**
 * Waar in een sectie citaties kunnen staan: een rich-text-veld, of de rich-text-kolommen van een
 * herhaalgroep (R-50). Een rich-text-kolom heeft in elke rij een eigen fragment (`rowFragment`).
 */
export type CitationField = FieldId | { readonly herhaalgroep: FieldId; readonly kolommen: readonly FieldId[] }

/**
 * De plekken met citaties in een sectie, in sectievolgorde: elk rich-text-veld, en elke herhaalgroep
 * met een rich-text-kolom. Eén afleiding voor het scherm en de pod, zodat ze dezelfde citaties tellen.
 */
export function citationFieldsOf(fields: readonly Field[]): CitationField[] {
  return fields.flatMap((field): CitationField[] => {
    if (field.control === 'richtext') return [field.id]
    if (field.control !== 'herhaalgroep') return []
    const kolommen = field.rijVelden.filter((rowField) => rowField.control === 'richtext').map((rowField) => rowField.id)
    return kolommen.length === 0 ? [] : [{ herhaalgroep: field.id, kolommen }]
  })
}

/**
 * Verzamelt de citatieclusters van één sectiedocument, in veldvolgorde; in een herhaalgroep rij na
 * rij en daarbinnen kolom na kolom, zoals ze op het scherm staan.
 *
 * Eén sectiedocument per aanroep, want de documentgrens is de sectie (B6). De nummering loopt wel
 * over het voorstel, zoals in CitatenPOC: `citationsOf` (`sectionCitations.ts`) zet de clusters van
 * de secties van een laag achter elkaar, in formuliervolgorde.
 */
export function collectClusters(doc: Y.Doc, fields: readonly CitationField[]): CitationCluster[] {
  return fields.flatMap((field) => {
    if (typeof field === 'string') return collectClustersFromFragment(richtextFragment(doc, field))
    // In de getoonde volgorde (`orderedRows`): na het verplaatsen van een rij nummeren de citaten mee.
    return orderedRows(rowsList(doc, field.herhaalgroep))
      .flatMap((row) =>
        field.kolommen.flatMap((column) => {
          const fragment = rowFragment(row, column)
          return fragment === undefined ? [] : collectClustersFromFragment(fragment)
        }),
      )
  })
}
